import React, { useState } from 'react';
import Slack from '@svg/slack';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';

import { Button, LoadingButton, Modal } from '@common/index';
import { createSlackLoader, SlackLoaderChannel, SlackLoaderChannelConfig } from '@requests/loaders';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../../../styles';
import { useParams } from 'react-router-dom';
import { Stack, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Card } from '@common/Card';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TagOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { State } from '@store/index';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
  }

  gap: 1.5rem;

  & .MuiDataGrid-row.Mui-selected {
    background-color: #e8f5fa !important;
  }
`;

const SlackCards = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: ${(props) => (props.columns === 1 ? '1fr' : '1fr 1fr')};
  gap: 1rem;
`;

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    secretIdentifier: string;
    selected: SlackLoaderChannel[];
    setSelected: React.Dispatch<React.SetStateAction<SlackLoaderChannel[]>>;
    region: string;
    refreshInterval: string;
  };
}

export const SlackChannelReview: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userId = useSelector((state: State) => state.auth.user_id);
  const orgId = useSelector((state: State) => state.auth.org_id);

  const { enqueueSnackbar } = useSnackbar();
  const { service_id, bucketName } = useParams();
  const { secretIdentifier: secretName, selected, region, refreshInterval } = configuration;

  const [channels, setChannels] = useState<SlackLoaderChannelConfig[]>(
    selected.map((item) => ({
      ...item,
      max_messages: 10,
      oldest_message: undefined,
      newest_message: undefined,
      enabled: true,
    }))
  );

  const { mutateAsync: createJob, isPending } = useMutation({
    mutationFn: createSlackLoader,
    onSuccess: (data) => {
      if (data.successful.length === 0) {
        enqueueSnackbar('Failed to create a job for all selected channels.', { variant: 'error' });
      }

      if (data.failed.length > 0) {
        enqueueSnackbar(`Channels failed to load: ${data.failed.join(', ')}`, { variant: 'error' });
      }

      if (data.successful.length > 0) {
        enqueueSnackbar(`Slack loader job configured successfully for channels (${data.successful.join(', ')}).`, {
          variant: 'success',
        });

        reset();
      }

      queryClient.invalidateQueries({ queryKey: ['globalFiles', 'bucket-loader'] });
    },
    onError: (error) => {
      enqueueSnackbar('Failed to setup Slack loader.', { variant: 'error' });
      enqueueSnackbar(error.message || 'An unexpected error occurred.', { variant: 'error' });
    },
  });

  const startJob = async () => {
    const channelConfigs = channels
      .filter((channel) => channel.enabled)
      .map((channel) => ({
        channel_id: channel.id,
        max_messages: channel.max_messages,
        oldest_message: channel.oldest_message ? channel.oldest_message.unix() : undefined,
        newest_message: channel.newest_message ? channel.newest_message.unix() : undefined,
      }));

    try {
      await createJob({
        service_id: service_id as string,
        bucket_name: bucketName as string,
        channels: channelConfigs,
        region: region as string,
        schedule: refreshInterval,
        secret_name: secretName,
        user_id: userId,
        org_id: orgId,
      });
    } catch {}
  };

  const body = (
    <Container>
      <p>
        Configure the message retrieval parameters for each channel. The maximum message count determines the total
        number of messages to be retrieved, while the oldest message date establishes the historical boundary for data
        collection.
      </p>
      <SlackCards columns={channels.length}>
        {channels.map((channel) => (
          <SlackChannelCard channel={channel} key={channel.id} setChannels={setChannels} />
        ))}
      </SlackCards>
    </Container>
  );

  const actions = (
    <Stack width='100%' direction='row' display='flex' justifyContent='flex-end'>
      <Stack spacing={1} direction='row'>
        <Button onClick={() => setPage(2)}>Back</Button>
        <LoadingButton
          loading={isPending}
          minHeight='32px'
          minWidth='140px'
          disabled={selected.length === 0}
          customColor='primary'
          onClick={startJob}
        >
          Load Channels
        </LoadingButton>
      </Stack>
    </Stack>
  );

  return (
    <Modal
      open={open === 'slack' && page === 3}
      setOpen={reset}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.06)}
      header='Slack'
      icon={<Slack color={theme.text.standard} height={21} />}
      body={body}
      minWidth={channels.length > 1 ? '1000px' : undefined}
      actions={actions}
    />
  );
};

const SlackChannelCard: React.FC<{
  channel: SlackLoaderChannelConfig;
  setChannels: React.Dispatch<React.SetStateAction<SlackLoaderChannelConfig[]>>;
}> = ({ channel, setChannels }) => {
  const [options, setOptions] = useState<('all' | 'newest' | 'oldest')[]>(['all']);

  const handleChange = (key: keyof SlackLoaderChannelConfig, value: number | Dayjs | boolean | undefined) => {
    setChannels((prev) => prev.map((item) => (item.id === channel.id ? { ...item, [key]: value } : item)));
  };

  const handleOptionsChange = (value: ('all' | 'newest' | 'oldest')[]) => {
    if (value.includes('all') && !options.includes('all')) setOptions(['all']);
    else if (value.includes('newest')) setOptions(value.filter((item) => item !== 'all'));
    else if (value.includes('oldest')) setOptions(value.filter((item) => item !== 'all'));
    else setOptions(value);

    if (!value.includes('oldest')) handleChange('oldest_message', undefined);
    else if (value.includes('oldest') && !channel.oldest_message)
      handleChange('oldest_message', dayjs().subtract(6, 'months'));

    if (!value.includes('newest')) handleChange('newest_message', undefined);
    else if (value.includes('newest') && !channel.newest_message) handleChange('newest_message', dayjs());

    if (value.includes('all') && !options.includes('all')) {
      handleChange('oldest_message', undefined);
      handleChange('newest_message', undefined);
    }

    if (value.length === 0) {
      setOptions(['all']);
      handleChange('oldest_message', undefined);
      handleChange('newest_message', undefined);
    }
  };

  const getGridTemplateColumns = () => {
    if (options.includes('newest') && options.includes('oldest')) return '1fr 1fr';
    return '1fr';
  };

  return (
    <Card
      title={channel.name}
      enabled={channel.enabled}
      icon={<TagOutlined />}
      tooltip='Use the switch to enable or disable the channel. Add newest or oldest message date to cap the number of messages retrieved.'
      setEnabled={(value) => handleChange('enabled', value)}
      body={
        <Container>
          <ToggleButtonGroup
            size='small'
            value={options}
            fullWidth
            onChange={(_, value) => handleOptionsChange(value)}
            color='primary'
          >
            <ToggleButton value='all'>All</ToggleButton>
            <ToggleButton value='oldest'>Cap Oldest</ToggleButton>
            <ToggleButton value='newest'>Cap Newest</ToggleButton>
          </ToggleButtonGroup>
          {!options.includes('all') && (
            <Stack display='grid' gridTemplateColumns={getGridTemplateColumns()} gap={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {options.includes('oldest') && (
                  <DatePicker
                    label='Oldest Message'
                    value={channel.oldest_message}
                    onChange={(value) => value && handleChange('oldest_message', value)}
                  />
                )}
                {options.includes('newest') && (
                  <DatePicker
                    label='Newest Message'
                    value={channel.newest_message}
                    onChange={(value) => value && handleChange('newest_message', value)}
                  />
                )}
              </LocalizationProvider>
            </Stack>
          )}
        </Container>
      }
    />
  );
};
